import {useQuery, useQueryClient} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'

export interface WalletTransaction {
  id: string
  type: 'debit' | 'credit' | 'platform_fee' | 'bill_purchase'
  amount: number
  description: string
  sessionId: string | null
  counterpartyId: string | null
  timestamp: number
  signature: string
  balanceAfter: number
}

export interface WalletResponse {
  balance: number
  transactions: WalletTransaction[]
}

const RQKEY_ROOT = 'wallet'
export const RQKEY = () => [RQKEY_ROOT]

interface RawTx {
  type: string
  price: number
  description: string
  balanceAfter?: number
  signature?: string
  counterpartyDid?: string
  createdAt: string
}

interface RawWalletResponse {
  wallet?: {
    solde?: number | string
    currency?: string
    conversionRatio?: number
  }
  transactions?: RawTx[]
}

function stableId(tx: RawTx): string {
  // Deterministic ID from stable server fields — avoids React key churn on re-fetches.
  const raw = `${tx.createdAt}|${tx.price}|${tx.description}`
  let h = 0
  for (let i = 0; i < raw.length; i++) {
    h = Math.imul(31, h) + raw.charCodeAt(i)
  }
  return `tx_${(h >>> 0).toString(16)}`
}

export function useWalletQuery() {
  const agent = useAgent()

  return useQuery({
    queryKey: RQKEY(),
    queryFn: async (): Promise<WalletResponse> => {
      const accessJwt = agent.session?.accessJwt
      if (!accessJwt) throw new Error('Not authenticated')

      const res = await fetch(
        `${BSKY_SERVICE}/xrpc/app.bsky.wallet.getWallet`,
        {
          headers: {
            Authorization: `Bearer ${accessJwt}`,
          },
        },
      )

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as {error?: string}).error ?? `HTTP ${res.status}`)
      }

      const raw = (await res.json()) as RawWalletResponse
      const w = raw.wallet ?? {}

      const transactions: WalletTransaction[] = (raw.transactions ?? []).map(
        (tx: RawTx) => ({
          id: stableId(tx),
          type: tx.type as WalletTransaction['type'],
          amount: tx.price,
          description: tx.description ?? '',
          sessionId: null,
          counterpartyId: tx.counterpartyDid ?? null,
          timestamp: new Date(tx.createdAt).getTime(),
          signature: tx.signature ?? '',
          balanceAfter: tx.balanceAfter ?? 0,
        }),
      )

      const solde = w.solde
      return {
        balance: typeof solde === 'string' ? parseFloat(solde) : (solde ?? 0),
        transactions,
      }
    },
    staleTime: STALE.MINUTES.ONE,
    enabled: !!agent.session?.accessJwt,
  })
}

export function useInvalidateWallet() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: RQKEY()})
}
